import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { useIntl } from 'react-intl';
import { useTranslations } from '@/i18n/use-translations';
import type { Delta } from './metrics-math';
import { sumClasses, TOKEN_CLASS_LABEL, TOKEN_CLASSES, type ClassValues, type TokenClass } from './pricing';

/**
 * The decomposition parts of the IMM-911 Metriken page: a 100 % stack over the
 * four token classes, because cache reads are three orders of magnitude above
 * output, and a chart of the token sum alone is a chart of cache reads with
 * everything else invisible.
 *
 * Colours are the kit's neutral ramp, ordinal by unit price. The lightest step
 * sits under 3:1 against the surface, so the bar is framed and every class is
 * named in the legend — identity never by colour alone.
 */
export const CLASS_STYLE: Readonly<Record<TokenClass, { readonly fill: string; readonly ink: string }>> = {
  cacheRead: { fill: 'var(--color-neutral-200)', ink: 'var(--text-secondary)' },
  uncachedInput: { fill: 'var(--color-neutral-400)', ink: 'var(--color-interactive-on)' },
  cacheWrite: { fill: 'var(--color-neutral-600)', ink: 'var(--color-interactive-on)' },
  output: { fill: 'var(--color-neutral-900)', ink: 'var(--color-interactive-on)' },
};

/** The class's colour chip, framed: the lightest step is under 3:1 against the surface. */
export function ClassSwatch({ tokenClass, size = 10 }: { readonly tokenClass: TokenClass; readonly size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: 2,
        background: CLASS_STYLE[tokenClass].fill,
        border: '1px solid var(--color-neutral-300)',
        display: 'inline-block',
      }}
    />
  );
}

/** A segment narrower than this carries no label; the legend names it. */
const LABEL_MIN_PERCENT = 7;

/**
 * …and neither does one too narrow in PIXELS for its label: 7 % of a phone-width
 * bar is ~20 px, and a clipped "12,4 %" reads as a different number.
 */
const LABEL_MIN_PX = 52;

/** The bar's rendered width, or `null` where layout cannot be measured. */
function useWidth<E extends HTMLElement>(): [RefObject<E | null>, number | null] {
  const ref = useRef<E>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;

    if (element === null || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? null));

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

const MONO: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

export type MetricFormat = {
  /** Every digit: `29.222.996`. */
  readonly tokens: (value: number) => string;
  /** `1,67 Mrd` · `6,9 Mio` · `32 Tsd` · `412`. */
  readonly tokensShort: (value: number) => string;
  /** A class share: one decimal, two below 10 %. */
  readonly percent: (share: number) => string;
  /** A whole-percent share: `41 %`, `<1 %`. */
  readonly percentWhole: (share: number) => string;
  readonly eur: (value: number) => string;
  readonly duration: (ms: number) => string;
  readonly delta: (value: Delta) => string;
  readonly count: (value: number) => string;
};

const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;
const MINUTE_MS = 60_000;
const HOUR_MINUTES = 60;

/** Locale-aware number formatting for the Metriken page — German by default. */
export function useMetricFormat(): MetricFormat {
  const intl = useIntl();
  const t = useTranslations('pages.admin.fleet-metrics');

  return useMemo(() => {
    const decimal = (value: number, digits: number) => intl.formatNumber(value, { maximumFractionDigits: digits });
    const percent = (share: number, digits: number) =>
      intl.formatNumber(share, { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: digits });

    return {
      tokens: (value) => intl.formatNumber(value),
      tokensShort: (value) => {
        if (value >= BILLION) return t('tokens-billion', { value: decimal(value / BILLION, 2) });

        if (value >= MILLION) return t('tokens-million', { value: decimal(value / MILLION, 1) });

        if (value >= THOUSAND) return t('tokens-thousand', { value: decimal(value / THOUSAND, 0) });

        return intl.formatNumber(value);
      },
      percent: (share) => percent(share, share < 0.1 ? 2 : 1),
      percentWhole: (share) => (share > 0 && share < 0.01 ? `<${percent(0.01, 0)}` : percent(share, 0)),
      eur: (value) =>
        intl.formatNumber(value, {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
      duration: (ms) => {
        if (ms < MINUTE_MS) return t('duration-seconds', { seconds: Math.round(ms / 1000) });

        const minutes = Math.round(ms / MINUTE_MS);

        if (minutes < HOUR_MINUTES) return t('duration-minutes', { minutes });

        return t('duration-hours', { hours: Math.floor(minutes / HOUR_MINUTES), minutes: minutes % HOUR_MINUTES });
      },
      delta: (value) => {
        if (value.kind === 'new') return t('delta-new');

        if (value.value === 0) return `±${percent(0, 0)}`;

        return `${value.value > 0 ? '+' : '−'}${percent(Math.abs(value.value) / 100, 0)}`;
      },
      count: (value) => intl.formatNumber(value),
    };
  }, [intl, t]);
}

/**
 * The `≈` — "this sum is a lower bound". `show` has NO default on purpose: a
 * forgotten prop must never dress an exact number up as an estimate.
 */
export function Approx({ show, title }: { readonly show: boolean; readonly title: string }) {
  if (!show) return null;

  return (
    <span title={title} aria-label={title} style={{ ...MONO, color: 'var(--text-tertiary)', marginRight: 2 }}>
      ≈
    </span>
  );
}

export function ClassBar({
  values,
  height = 28,
  labels = true,
}: {
  readonly values: ClassValues;
  readonly height?: number;
  readonly labels?: boolean;
}) {
  const format = useMetricFormat();
  const total = sumClasses(values);
  const [ref, width] = useWidth<HTMLDivElement>();

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        width: '100%',
        height,
        gap: 2,
        border: '1px solid var(--color-neutral-200)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        background: 'var(--bg-surface)',
      }}
    >
      {total > 0 &&
        TOKEN_CLASSES.map((key) => {
          const share = values[key] / total;

          if (share <= 0) return null;

          return (
            <div
              key={key}
              data-token-class={key}
              style={{
                width: `${share * 100}%`,
                minWidth: 1,
                background: CLASS_STYLE[key].fill,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {labels && share * 100 >= LABEL_MIN_PERCENT && (width === null || share * width >= LABEL_MIN_PX) && (
                <span
                  style={{
                    ...MONO,
                    fontSize: 'var(--text-xs)',
                    color: CLASS_STYLE[key].ink,
                    padding: '0 var(--space-1)',
                  }}
                >
                  {format.percent(share)}
                </span>
              )}
            </div>
          );
        })}
    </div>
  );
}

/** One line per class: name, amount, share — the stack's legend, stacked in a column. */
export function ClassLegend({ values }: { readonly values: ClassValues }) {
  const format = useMetricFormat();
  const total = sumClasses(values);

  return (
    <ul className="flex flex-col gap-1" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {TOKEN_CLASSES.map((key) => (
        <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)', minWidth: 0 }}>
          <ClassSwatch tokenClass={key} />
          <span style={{ ...MONO, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
            {TOKEN_CLASS_LABEL[key]}
          </span>
          <span style={{ ...MONO, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
            {format.tokensShort(values[key])}
          </span>
          {total > 0 && (
            <span style={{ ...MONO, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
              {format.percent(values[key] / total)}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function Eyebrow({ children }: { readonly children: ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--text-xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-tertiary)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export const MONO_STYLE = MONO;
